import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlagId } from 'config/shared/enums/featureFlagIds';
import { Themes, ResolvedTheme } from '@cko/dashboard-shared';

type ThemeClasses = Record<ResolvedTheme, string>;

type ThemeContextType = {
  theme: Themes;
  updateTheme: (newTheme: Themes) => void;
};

export const THEME_LOCAL_STORAGE_KEY = 'cko-dashboard-user-theme';
export const THEME_CLASSES: ThemeClasses = {
  light: '',
  dark: 'dark-mode',
};

export const saveThemeToLocalStorage = (theme: Themes) => {
  localStorage.setItem(THEME_LOCAL_STORAGE_KEY, theme);
};

export const getThemeFromLocalStorage = (): Themes | null => {
  const theme: string | null = localStorage.getItem(THEME_LOCAL_STORAGE_KEY);
  if (theme !== null) {
    return theme as Themes;
  } else {
    return theme;
  }
};

export const getDefaultTheme = (): Themes => {
  return 'system-default';
};

const getSystemPreference = (): Themes => {
  if (window?.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const resolveTheme = (theme: Themes): ResolvedTheme => {
  const systemPrefence = getSystemPreference();
  return theme === 'dark' || (theme === 'system-default' && systemPrefence === 'dark')
    ? 'dark'
    : 'light';
};

const applyTheme = (resolvedTheme: ResolvedTheme) => {
  if (resolvedTheme === 'dark') {
    document.body.classList.add(THEME_CLASSES.dark);
  } else {
    document.body.classList.remove(THEME_CLASSES.dark);
  }
};

const ThemeContext = createContext<ThemeContextType>({
  theme: 'system-default',
  updateTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<Themes>(() => getThemeFromLocalStorage() || getDefaultTheme());

  const updateTheme = useCallback(
    (newTheme: Themes) => {
      saveThemeToLocalStorage(newTheme);
      setTheme(newTheme);
    },
    [setTheme],
  );

  const flags = useFlags();
  const isThemeSelectionEnabled = flags[FeatureFlagId.DARK_MODE];

  useEffect(() => {
    if (isThemeSelectionEnabled) {
      const resolved = resolveTheme(theme);
      applyTheme(resolved);
    }
  }, [theme, isThemeSelectionEnabled]);

  return <ThemeContext.Provider value={{ theme, updateTheme }}>{children}</ThemeContext.Provider>;
};

export const useThemes = () => {
  return useContext(ThemeContext);
};
